import React, { useContext, useRef, useLayoutEffect, useEffect } from 'react';
import { useState } from 'react';

import { motion, AnimatePresence } from '../framer-motion-custom';
import * as styles from './Carousel.module.scss';
import { CarouselContext } from './CarouselProvider';

const transitions = {
  fade: {
    enter: {
      opacity: 0,
    },
    center: {
      opacity: 1,
    },
    exit: {
      opacity: 0,
    },
  },
  slide: {
    enter: ({ direction, width }) => {
      return {
        zIndex: 1,
        x: direction === 'right' ? width : -width,
      };
    },
    center: {
      zIndex: 1,
      x: 0,
    },
    exit: ({ direction, width }) => {
      return {
        zIndex: 0,
        x: direction === 'left' ? width : -width,
      };
    },
  },
};

const swipePower = (offset, velocity) => {
  return Math.abs(offset) * velocity;
};

const Slider = ({
  children,
  swipeConfidenceThreshold = 10000,
  autoHeight = true,
  transition = 'fade',
}) => {
  const { state, dispatch } = useContext(CarouselContext);
  const childArray = React.Children.toArray(children);
  const [ currentHeight, setHeight ] = useState(0);
  const [ allowScroll, setAllowScroll ] = useState(true);
  const ref = useRef(null);
  const slideRef = useRef(null);

  function handleAnimationComplete() {
    if (slideRef.current && state.currentIndex !== state.prevIndex) {
      slideRef.current.focus();
    }
  }

  function handleDragEnd(e, { offset, velocity }) {
    const swipe = swipePower(offset.x, velocity.x);

    if (swipe < -swipeConfidenceThreshold) {
      dispatch({ type: 'NEXT_SLIDE' });
    } else if (swipe > swipeConfidenceThreshold) {
      dispatch({ type: 'PREV_SLIDE' });
    }
  }

  // Set height to current item if auto height
  useEffect(() => {
    if (slideRef.current && autoHeight) {
      const slideHeight = slideRef.current.getBoundingClientRect().height;
      setHeight(slideHeight);
    }
  }, [ slideRef, state.currentIndex, autoHeight ]);

  // ...otherwise set the height to the first slide
  useEffect(() => {
    if (!autoHeight) {
      const slideHeight = slideRef.current.getBoundingClientRect().height;
      setHeight(slideHeight);
    }
  }, [ slideRef, autoHeight ]);

  useEffect(() => {
    if (allowScroll) {
      const handleTouch = (event) => {
        event.stopPropagation();
      };
      document.documentElement.addEventListener('touchmove', handleTouch);
      return () => {
        document.documentElement.removeEventListener('touchmove', handleTouch);
      };
    }
  }, [ allowScroll ]);

  return (
    <>
      {state.totalSlides > 1 && (
        <div className={styles.announcer} aria-live="polite" aria-atomic="true">
          Showing {state.currentIndex + 1} of {state.totalSlides} slides
        </div>
      )}

      <motion.div
        className={styles.slides}
        animate={{ height: currentHeight }}
        ref={ref}
      >
        <AnimatePresence initial={false}>
          <motion.div
            className={styles.slide}
            key={state.currentIndex}
            custom={{
              direction: state.direction,
              width: ref?.current?.getBoundingClientRect().width,
            }}
            variants={transitions[ transition ]}
            initial="enter"
            animate="center"
            exit="exit"
            transition={
              transition === 'slide'
                ? {
                    x: { type: 'spring', stiffness: 300, damping: 50 },
                    opacity: { duration: 0.2 },
                  }
                : null
            }
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragDirectionLock
            dragElastic={1}
            onDragStart={(event, info) => {
              setAllowScroll(Math.abs(info.delta.y) > Math.abs(info.delta.x));
            }}
            onDragEnd={handleDragEnd}
            layout={false}
            inherit={false}
            onAnimationComplete={handleAnimationComplete}
            tabIndex={-1}
            ref={slideRef}
          >
            {childArray[ state.currentIndex ]}
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </>
  );
};

export default Slider;
